import ReactGA from "react-ga4";
import { useAppStore } from '@/store';
import { trackEvent } from '@/lib/analytics';

const GA_MEASUREMENT_ID = "G-ZP3NZLMM3E";

/**
 * Initializes Google Analytics if the user has consented and a Measurement ID is provided.
 */
export const initGA = () => {
  const { allowAnalytics } = useAppStore.getState();
  if (allowAnalytics && GA_MEASUREMENT_ID) {
    if (!ReactGA.isInitialized) {
      ReactGA.initialize(GA_MEASUREMENT_ID, {
        gtagOptions: {
          send_page_view: false,
        },
      });
    }
  }
};

/**
 * Tracks a page view in Google Analytics.
 * @param path The path of the page to track.
 * @param pageTitle The human-readable page title to send with the page view.
 */
export const trackPageView = (path: string, pageTitle?: string) => {
  const { allowAnalytics } = useAppStore.getState();
  const title = pageTitle || (typeof document !== 'undefined' ? document.title : undefined);

  if (!allowAnalytics) return;

  if (ReactGA.isInitialized) {
    ReactGA.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: typeof window !== 'undefined' ? `${window.location.origin}${path}` : path,
    });
  }

  // Also track to Supabase analytics for map visualization.
  // This will work for both authenticated and guest users.
  trackEvent('page_view', { path, pageTitle: title });
};

/**
 * Tracks a custom event in Google Analytics.
 * @param category The category of the event.
 * @param action The action of the event.
 * @param label An optional label for the event.
 */
export const trackGAEvent = (category: string, action: string, label?: string) => {
  const { allowAnalytics } = useAppStore.getState();
  if (allowAnalytics && ReactGA.isInitialized) {
    ReactGA.event({
      category,
      action,
      label,
    });
  }
};